import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Archive } from 'lucide-react';
import { Button } from '../ui';
import './ProjectLimitWarning.css';

interface ProjectLimitWarningProps {
  currentCount: number;
  limit: number;
  onArchiveClick?: () => void;
  onManageClick?: () => void;
}

export const ProjectLimitWarning: React.FC<ProjectLimitWarningProps> = ({
  currentCount,
  limit,
  onArchiveClick,
  onManageClick,
}) => {
  const { t } = useTranslation();
  const isAtLimit = currentCount >= limit;
  const warningThreshold = limit - 10; // Show warning when 10 or fewer slots remain
  const isNearLimit = currentCount >= warningThreshold && currentCount < limit;
  const remaining = limit - currentCount;

  if (!isAtLimit && !isNearLimit) {
    return null;
  }

  return (
    <div
      className={`project-limit-warning ${isAtLimit ? 'project-limit-warning--at-limit' : 'project-limit-warning--near-limit'}`}
    >
      <div className="project-limit-warning__icon">
        <AlertTriangle size={20} />
      </div>
      <div className="project-limit-warning__content">
        <h4 className="project-limit-warning__title">
          {isAtLimit
            ? t('works.limit.reached')
            : t('works.limit.warning')}
        </h4>
        <p className="project-limit-warning__message">
          {isAtLimit
            ? t('works.limit.reachedMessage', { limit })
            : t('works.limit.message', { count: currentCount, limit, remaining })}
        </p>
      </div>
      <div className="project-limit-warning__actions">
        {onArchiveClick && (
          <Button variant="ghost" size="sm" onClick={onArchiveClick}>
            <Archive size={14} />
            {t('works.limit.archivePrompt')}
          </Button>
        )}
        {onManageClick && (
          <Button variant="ghost" size="sm" onClick={onManageClick}>
            {t('works.actions.viewDetails')}
          </Button>
        )}
      </div>
    </div>
  );
};

// Compact version for embedding in forms/modals
export const ProjectLimitBadge: React.FC<{
  currentCount: number;
  limit: number;
}> = ({ currentCount, limit }) => {
  const { t } = useTranslation();
  const isAtLimit = currentCount >= limit;
  const remaining = limit - currentCount;

  return (
    <span
      className={`project-limit-badge ${isAtLimit ? 'project-limit-badge--at-limit' : ''}`}
    >
      {remaining > 0 ? (
        <>
          {remaining} {remaining === 1 ? 'slot' : 'slots'} {t('common.active').toLowerCase()}
        </>
      ) : (
        <>{t('works.limit.reached')}</>
      )}
    </span>
  );
};

export default ProjectLimitWarning;
